import React, { memo, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { FiActivity, FiAlertCircle, FiChevronRight, FiDownload, FiPlus, FiSearch } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, INPUT, PageHeader, Pill, Segmented, SIZE, SkeletonRows } from '../ui/kit';
import { initials } from '../patients/patientModel';
import { echoAccess, examDate, operators, patientName, searchText } from './echoModel';
import { exportEchosExcel, exportEchosPdf } from './echoExport';

const PAGE_SIZE = 30;

const EchoRow = memo(({ entry }) => {
  const { echo, linked } = entry;
  const identite = echo.identite || {};
  const fe = echo.mensuration && echo.mensuration.fractionEjection;
  const meta = [entry.dateLabel, identite.age && `${identite.age} ans`, identite.sexe === 'F' ? 'Féminin' : identite.sexe === 'M' ? 'Masculin' : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="border-0 border-b-[1px] border-solid border-black/[0.05] last:border-b-0">
      <Link
        to={`/echo/${echo.id}`}
        className="flex items-center gap-[14px] px-[16px] py-[14px] text-ink no-underline transition hover:bg-black/[0.02] hover:text-ink sm:px-6"
      >
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-rose-50 text-[13px] font-semibold text-rose-600" aria-hidden="true">
          {initials(patientName(echo))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold">{patientName(echo)}</span>
          <span className="mt-0.5 block truncate text-[13px] text-black/45">{meta}</span>
          {(identite.diagnostic || operators(echo).length > 0) && (
            <span className="mt-0.5 block truncate text-[12.5px] text-black/40">
              {[identite.diagnostic, operators(echo).join(', ')].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
        <span className="hidden flex-none items-center gap-1.5 md:flex">
          {fe && <Pill tone="brand">FE {fe} %</Pill>}
          {linked ? <Pill tone="green">Dossier {linked.dossierNumber || linked.nom}</Pill> : <Pill tone="amber">Non rattachée</Pill>}
        </span>
        <FiChevronRight size={17} aria-hidden="true" className="flex-none text-black/25" />
      </Link>
    </li>
  );
});

const EchoList = () => {
  const user = useSessionUser();
  const access = echoAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [echos, setEchos] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [link, setLink] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    ensurePatients().catch(() => {});
    return onSnapshot(
      collection(db, 'echos'),
      (snapshot) => {
        setEchos(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setStatus('ready');
      },
      () => setStatus('error')
    );
  }, [ensurePatients]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const prepared = useMemo(
    () =>
      echos
        .map((echo) => {
          const linked = echo.patientId ? patientsById[echo.patientId] : null;
          return {
            echo,
            linked,
            time: new Date(examDate(echo) || 0).getTime() || 0,
            dateLabel: formatDate(examDate(echo)),
            search: searchText(echo, linked),
          };
        })
        .sort((a, b) => b.time - a.time),
    [echos, patientsById]
  );

  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(
    () =>
      prepared.filter(
        (entry) =>
          (!link || (link === 'linked' ? Boolean(entry.echo.patientId) : !entry.echo.patientId)) && (!query || entry.search.includes(query))
      ),
    [prepared, link, query]
  );
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [link, query]);

  const unlinked = prepared.filter((entry) => !entry.echo.patientId).length;

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Soins"
        title="Échographies"
        subtitle={status === 'ready' ? `${shown.length} compte${shown.length > 1 ? 's' : ''} rendu${shown.length > 1 ? 's' : ''} d’écho-doppler cardiaque` : 'Chargement des échographies…'}
        actions={
          <>
            {access.canExport && (
              <>
                <button type="button" onClick={() => exportEchosExcel(shown.map((entry) => entry.echo), patientsById)} disabled={!shown.length} className={`${BUTTON.secondary} ${SIZE.md}`}>
                  <FiDownload size={15} aria-hidden="true" />
                  Excel
                </button>
                <button type="button" onClick={() => exportEchosPdf(shown.map((entry) => entry.echo))} disabled={!shown.length} className={`${BUTTON.secondary} ${SIZE.md}`}>
                  <FiDownload size={15} aria-hidden="true" />
                  PDF
                </button>
              </>
            )}
            {access.canWrite && (
              <Link to="/echo/nouvelle" className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiPlus size={16} aria-hidden="true" />
                Nouvelle écho
              </Link>
            )}
          </>
        }
      />

      <div className="mt-8 flex flex-col gap-[12px] lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, diagnostic, opérateur, conclusion, n° de dossier…"
            aria-label="Rechercher une échographie"
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <Segmented
          value={link}
          onChange={setLink}
          allowEmpty={false}
          options={[
            { value: '', label: 'Toutes' },
            { value: 'linked', label: 'Rattachées' },
            { value: 'unlinked', label: `À rattacher${unlinked ? ` (${unlinked})` : ''}` },
          ]}
        />
      </div>

      <section className={`${CARD} mt-[20px] overflow-hidden`}>
        {status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={6} />
          </div>
        ) : status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title="Échographies indisponibles">
            Elles n’ont pas pu être chargées. Vérifiez la connexion, puis rechargez la page.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiActivity} title="Aucune échographie">
            {echos.length ? 'Aucune échographie ne correspond à cette recherche.' : 'Les comptes rendus d’écho apparaîtront ici.'}
          </EmptyState>
        ) : (
          <>
            <ul className="m-0 list-none p-0">
              {shown.slice(0, limit).map((entry) => (
                <EchoRow key={entry.echo.id} entry={entry} />
              ))}
            </ul>
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - limit)} échographies de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichées
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default EchoList;
